import { buildStudyJpegZip } from './buildStudyJpegZip';
import { triggerBrowserDownload } from './buildStudyDicomZipFilename';
import { collectStudyImageExportTargets } from './collectStudyImageTargets';
import { classifyDownloadError } from './downloadError';
import { DOWNLOAD_MESSAGES } from './downloadMessages';
import { resolveStudyInstanceUIDFromViewport } from './resolveViewportStudy';
import type { DicomZipProgressCallback } from './types';

export type DownloadStudyJpegZipOptions = {
  viewportId: string;
  servicesManager: AppTypes.ServicesManager;
  extensionManager: AppTypes.ExtensionManager;
  onProgress?: DicomZipProgressCallback;
};

export type DownloadStudyJpegZipResult =
  | { success: true; filename: string; imageCount: number }
  | { success: false; message: string };

export async function downloadStudyJpegZip({
  viewportId,
  servicesManager,
  extensionManager,
  onProgress,
}: DownloadStudyJpegZipOptions): Promise<DownloadStudyJpegZipResult> {
  const studyInstanceUID = resolveStudyInstanceUIDFromViewport(viewportId, servicesManager);

  if (!studyInstanceUID) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_JPEG_AVAILABLE };
  }

  const targets = collectStudyImageExportTargets(
    studyInstanceUID,
    servicesManager,
    extensionManager
  );

  if (!targets.length) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_JPEG_AVAILABLE };
  }

  try {
    const { blob, filename, includedCount } = await buildStudyJpegZip(
      studyInstanceUID,
      targets,
      extensionManager,
      onProgress,
      servicesManager
    );

    if (!includedCount) {
      return { success: false, message: DOWNLOAD_MESSAGES.JPEG_CONVERSION_FAILED };
    }

    triggerBrowserDownload(blob, filename);

    if (process.env.NODE_ENV !== 'production') {
      console.info('[ViewportDownload] Study JPEG ZIP export complete', {
        studyInstanceUID,
        filename,
        imageCount: includedCount,
      });
    }

    return { success: true, filename, imageCount: includedCount };
  } catch (error) {
    console.error('[ViewportDownload] Study JPEG ZIP export failed:', error);
    return { success: false, message: classifyDownloadError(error) };
  }
}
